/**
 * useVyasaMonitor — polls /api/strategies/vyasa/state at 1 Hz.
 *
 * Returns the full state payload + a fetchedAt timestamp + connection status.
 * Cleaner alternative to V4's SSE-driven createStrategyMonitorHook because
 * the Python service writes to MySQL at >= 1 Hz cadence so a 1s poll
 * matches the data freshness and is much simpler to reason about.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import resolveApiBase from '../tradingCockpit/apiBase';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;
const POLL_INTERVAL_MS = 1000;

const resolveUrl = (path) => {
    if (!API_BASE) return path;
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const INITIAL_PAYLOAD = {
    strategyId: 'vyasa_v1',
    strategyLabel: 'Vyasa',
    fetchedAt: null,
    tradingDayStart: null,
    state: {
        state: 'initialising',
        stateChangedAt: null,
        counters: { trades: 0, open: 0, blockedBook: 0, blockedCap: 0 },
        pnl: { realized: 0, unrealized: 0, total: 0 },
        modelVersions: null,
        modelScores: null,
        haltReason: null,
        lastHeartbeatAt: null,
    },
    legs: [],
    history: [],
    historyTotals: { realizedNet: 0, realizedGross: 0, tcaCost: 0, closedCount: 0 },
    liveUnrealizedFromLegs: 0,
    events: [],
};

export function useVyasaMonitor({ pollIntervalMs = POLL_INTERVAL_MS } = {}) {
    const [payload, setPayload] = useState(INITIAL_PAYLOAD);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const aliveRef = useRef(true);
    const inFlightRef = useRef(false);

    const fetchOnce = useCallback(async () => {
        if (inFlightRef.current) return;        // Skip overlap if previous slow
        inFlightRef.current = true;
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (API_KEY) headers['x-api-key'] = API_KEY;
            const resp = await fetch(resolveUrl('/api/strategies/vyasa/state'), {
                method: 'GET',
                headers,
                credentials: 'include',
                cache: 'no-store',
            });
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const data = await resp.json();
            if (!aliveRef.current) return;
            setPayload(data);
            setConnected(true);
            setError(null);
        } catch (err) {
            if (!aliveRef.current) return;
            setConnected(false);
            setError(err.message || String(err));
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        aliveRef.current = true;
        fetchOnce();
        const id = window.setInterval(fetchOnce, pollIntervalMs);
        return () => {
            aliveRef.current = false;
            window.clearInterval(id);
        };
    }, [fetchOnce, pollIntervalMs]);

    return {
        ...payload,
        connected,
        error,
        refresh: fetchOnce,
    };
}

export default useVyasaMonitor;
